import { z } from 'zod'
import { reactive, set, del, nextTick } from 'vue'
import { parseValidationObject } from './schemas'
import { ValidationValue } from './plugin'

// Error structure
export interface ValidationError {
  field: string
  message: string
}

type FieldOptions = Map<string, string>
type Fields = Map<
  string,
  {
    schema: z.ZodType<unknown>
    element: HTMLElement | null
    opts: FieldOptions
  }
>

// Validator state
export interface ValidatorState {
  dirty: boolean // Track if any validation has run
  errors: Record<string, string>
  fields: Fields
  paused: boolean
  touched: Set<string>
  values: Record<string, unknown>
}

// Public validator API
export class Validator {
  private state: ValidatorState

  constructor() {
    // Make state reactive using the Composition API
    // This works in both Vue 2.7+ and Vue 3
    this.state = reactive({
      values: {},
      errors: {},
      touched: new Set(),
      dirty: false,
      fields: new Map(),
      paused: false,
    })
  }

  // Core validation methods
  async validate(): Promise<boolean> {
    if (this.state.paused) return true

    // Mark validation as run
    this.state.dirty = true

    let isValid = true

    for (const [field, { schema }] of this.state.fields.entries()) {
      // Mark all fields as touched when validating the entire form
      this.state.touched.add(field)

      const value = this.state.values[field]
      const result = schema.safeParse(value)

      if (result.success) {
        // Vue 2.7 and 3 have different behaviors with delete
        // Using del helper ensures compatibility
        del(this.state.errors, field)
      } else {
        const message = result.error.issues[0]?.message || 'Invalid value'
        // Vue 2.7 and 3 have different behaviors with property assignment
        // Using set helper ensures compatibility
        set(this.state.errors, field, message)
        isValid = false
      }
    }

    // Ensure reactivity in both Vue 2.7 and Vue 3
    await nextTick()
    return isValid
  }

  async validateAll(): Promise<boolean> {
    return this.validate()
  }

  async validateField(field: string): Promise<boolean> {
    if (this.state.paused) return true

    const fieldData = this.state.fields.get(field)
    if (!fieldData) return true

    // Mark this specific field as touched
    this.state.touched.add(field)
    // Mark validation as run
    this.state.dirty = true

    const value = this.state.values[field]
    const result = fieldData.schema.safeParse(value)

    if (result.success) {
      del(this.state.errors, field)
      await nextTick()
      return true
    } else {
      const message = result.error.issues[0]?.message || 'Invalid value'
      set(this.state.errors, field, message)
      await nextTick()
      return false
    }
  }

  // Field registration methods
  addField(
    field: string,
    validation: ValidationValue,
    element: HTMLElement | null = null,
    opts?: FieldOptions,
  ): void {
    let schema: z.ZodType<unknown>
    const fieldOpts = opts ?? new Map()

    if (validation instanceof z.ZodType) {
      schema = validation
    } else {
      schema = parseValidationObject(validation)
    }

    this.state.fields.set(field, { schema, element, opts: fieldOpts })
  }

  removeField(field: string): void {
    this.state.fields.delete(field)
    del(this.state.errors, field)
    del(this.state.values, field)
    this.state.touched.delete(field)
  }

  // Value management
  setValue(field: string, value: unknown): void {
    set(this.state.values, field, value)
  }

  getValue(field: string): unknown {
    return this.state.values[field]
  }

  // Error management
  hasError(field: string): boolean {
    // Only consider a field as having an error if it's been touched
    const hasTouched = this.state.touched.has(field)
    const hasError = field in this.state.errors
    return hasTouched && hasError
  }

  hasAnyError(): boolean {
    return Object.keys(this.state.errors).length > 0
  }

  getError(field: string): string | null {
    // Only return the error if the field has been touched
    if (!this.state.touched.has(field)) return null
    return this.state.errors[field] || null
  }

  getAllErrors(): ValidationError[] {
    return Object.entries(this.state.errors).map(([field, message]) => {
      const fieldObj = this.state.fields.get(field)
      const fieldName = fieldObj.opts.get('as') ?? field

      return {
        field: fieldName,
        message,
      }
    })
  }

  getFullMessages(): string[] {
    return this.getAllErrors().map((error) => `${error.field}: ${error.message}`)
  }

  // Lifecycle methods
  reset(): void {
    // Use set to ensure reactivity in both Vue 2.7 and Vue 3
    set(this.state, 'errors', {})
    this.state.touched.clear()
    this.state.dirty = false
  }

  resetField(field: string): void {
    del(this.state.errors, field)
    this.state.touched.delete(field)
  }

  pause(): void {
    this.state.paused = true
  }

  resume(): void {
    this.state.paused = false
  }

  // Field state tracking
  isFieldTouched(field: string): boolean {
    return this.state.touched.has(field)
  }

  getFieldState(field: string): boolean | null {
    const isDirty = this.state.dirty
    const isTouched = this.isFieldTouched(field)

    // If validation hasn't run or field not touched, return null (no validation state)
    if (!isDirty || !isTouched) return null

    // If has error, return false (invalid)
    if (this.hasError(field)) return false

    // Otherwise return true (valid)
    return true
  }

  // Error API for templates
  get errors() {
    // In both Vue 2.7 and Vue 3, this works because the Composition API
    // makes the resulting object reactive
    return {
      has: (field: string) => {
        return this.hasError(field)
      },
      first: (field: string) => {
        return this.getError(field)
      },
      count: () => {
        return Object.keys(this.state.errors).length
      },
      // Get validation state for bootstrap forms (true=valid, false=invalid, null=not validated)
      getFieldState: (field: string) => {
        return this.getFieldState(field)
      },
    }
  }
}

// Factory function
export const createValidator = () => new Validator()
